import React, { useEffect, useRef, useState } from "react";
import { useSelector } from 'react-redux';
import { Canvas, useThree } from '@react-three/fiber';
import StageBackground from "./StageBackground";
import Grid3D from "./Grid3D";
import Mask3D from "./Mask3D";
import Contours3D from "./Contours3D";
import checkForOverlap from "../overlapCheck.component";
import MastHandler3D from "./MastTypes/MastHandler3D";
import CameraControls from 'camera-controls';
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

CameraControls.install({ THREE });

const CameraControlHandler = ({
    cameraCommand,
    setCameraCommand,
    savedCameraPosition,
    setSavedCameraPosition,
    realWidth,
    realHeight
}) => {
    const { camera, gl } = useThree();
    const controlsRef = useRef();

    useEffect(() => {
        const controls = new CameraControls(camera, gl.domElement);
        controlsRef.current = controls;

        const distance = Math.max(realWidth, realHeight);
        const centerX = 0;
        const centerZ = 0;
        const startX = centerX;
        const startY = distance * 1.2;
        const startZ = centerZ;
        const lookAtY = -distance * 0.1;

        controls.setLookAt(startX, startY, startZ, centerX, lookAtY, centerZ, true);

        setSavedCameraPosition({
            position: new THREE.Vector3(startX, startY, startZ),
            lookAt: new THREE.Vector3(centerX, lookAtY, centerZ),
        });

        controls.minPolarAngle = 0;
        controls.maxPolarAngle = Math.PI / 2;
        controls.minDistance = 1;
        controls.maxDistance = 10000;

        controls.mouseButtons.left = CameraControls.ACTION.ROTATE;
        controls.mouseButtons.right = CameraControls.ACTION.TRUCK;
        controls.mouseButtons.middle = CameraControls.ACTION.DOLLY;

        controls.dampingFactor = 0.15;
        controls.dragToOffset = false;

        return () => {
            controls.dispose();
        };
    }, [camera, gl, realWidth, realHeight, setSavedCameraPosition]);


    useEffect(() => {
        if (!savedCameraPosition && camera) {
            setSavedCameraPosition({
                position: camera.position.clone(),
                rotation: camera.rotation.clone(),
            });
        }
    }, [camera, savedCameraPosition, setSavedCameraPosition]);

    useEffect(() => {
        if (!cameraCommand || !controlsRef.current) return;

        const controls = controlsRef.current;
        const distance = Math.max(realWidth, realHeight) / 2;
        const centerX = 0;
        const centerZ = 0;

        const commands = {
            top: new THREE.Vector3(centerX, distance, centerZ - distance),
            bottom: new THREE.Vector3(centerX, distance, centerZ + distance),
            left: new THREE.Vector3(centerX - distance, distance, centerZ),
            right: new THREE.Vector3(centerX + distance, distance, centerZ),
            iso1: new THREE.Vector3(centerX - distance, distance, centerZ - distance),
            iso2: new THREE.Vector3(centerX + distance, distance, centerZ - distance),
            iso3: new THREE.Vector3(centerX - distance, distance, centerZ + distance),
            iso4: new THREE.Vector3(centerX + distance, distance, centerZ + distance),
        };

        if (cameraCommand === 'reset' && savedCameraPosition) {
            const { position } = savedCameraPosition;
            const distance = Math.max(realWidth, realHeight);
            const elevatedY = position.y + distance * 0.2;
            const tiltY = -distance * 0.1;

            controls.setLookAt(
                position.x,
                elevatedY,
                position.z,
                centerX,
                tiltY,
                centerZ,
                true
            );
        } else if (commands[cameraCommand]) {
            const pos = commands[cameraCommand];
            controls.setLookAt(pos.x, pos.y, pos.z, centerX, 0, centerZ, true);
        }

        setCameraCommand(null);
    }, [cameraCommand, savedCameraPosition, setCameraCommand, realWidth, realHeight]);

    useFrame((_, delta) => {
        controlsRef.current?.update(delta);
    });

    return null;
};


const StageCanvas = ({
    image,
    realWidth,
    realHeight,
    savedCameraPosition,
    setSavedCameraPosition,
    grids,
    masks,
    columns,
    cameraCommand,
    setCameraCommand,
    areaResults = [],
    colorList,
    luminaires,
}) => {
    const containerRef = useRef();

    const cameraDistance = Math.max(realWidth, realHeight) * 1.2;
    const [overlapInfo, setOverlapInfo] = useState([]);
    const state = useSelector(state => state.ProjectConfig);

    useEffect(() => {
        const overlapInfo = checkForOverlap(masks);
        setOverlapInfo(overlapInfo);
    }, [masks]);

    useEffect(() => {
        const canvas = document.querySelector('canvas');
        if (!canvas) return;

        const handlePointerDown = (event) => {
            canvas.setPointerCapture(event.pointerId);
        };

        const handlePointerUp = (event) => {
            canvas.releasePointerCapture(event.pointerId);
        };

        canvas.addEventListener('pointerdown', handlePointerDown);
        canvas.addEventListener('pointerup', handlePointerUp);

        return () => {
            canvas.removeEventListener('pointerdown', handlePointerDown);
            canvas.removeEventListener('pointerup', handlePointerUp);
        };
    }, []);


    useEffect(() => {
        const resizeListener = () => {
            const canvas = document.querySelector('canvas');
            if (canvas && canvas.parentElement) {
                const ctx = canvas.getContext('webgl');
                if (ctx && ctx.drawingBufferWidth) {
                    ctx.viewport(0, 0, ctx.drawingBufferWidth, ctx.drawingBufferHeight);
                }
            }
        };

        window.addEventListener("resize", resizeListener);
        resizeListener();
        return () => window.removeEventListener("resize", resizeListener);
    }, []);

    return (
        <div ref={containerRef} style={{ width: "100%", height: "100%" }}>
            <Canvas
                camera={{
                    position: [0, 0, cameraDistance],
                    fov: 45,
                    near: 0.1,
                    far: 10000,
                }}
            >
                <ambientLight intensity={0.5} />
                <directionalLight position={[5, 10, 7]} intensity={1} />
                <StageBackground image={image} realWidth={realWidth} realHeight={realHeight} />

                <group
                    position={[-realWidth / 2, 0, -realHeight / 2]}
                    rotation={[-Math.PI / 2, 0, 0]}
                >
                    {grids
                        .filter((g) => g.type === "Horizontal" || g.type === "Vertical")
                        .map((grid, i) => (
                            <Grid3D key={i} grid={grid} gridScale={1} areaResults={areaResults} state={state} />
                        ))}

                    {masks?.map((mask, i) => (
                        <Mask3D key={i} mask={mask} gridScale={1} overlapInfo={overlapInfo} maskList={masks} />
                    ))}

                    {columns?.map((column, i) => (
                        <MastHandler3D key={i} column={column} gridScale={1} colorList={colorList} luminaires={luminaires} />
                    ))}

                    <Contours3D
                        colors={state.contourColors}
                        values={state.contourValues}
                        visible={state.showContours}
                        areaResults={areaResults}
                        grid={grids.filter((grid) => grid.name === "Grid #0")}
                        autoContourValues={state.autoContourValues}
                    />
                </group>

                <CameraControlHandler
                    cameraCommand={cameraCommand}
                    setCameraCommand={setCameraCommand}
                    savedCameraPosition={savedCameraPosition}
                    setSavedCameraPosition={setSavedCameraPosition}
                    realWidth={realWidth}
                    realHeight={realHeight}
                />

            </Canvas>
        </div>
    );
};

export default StageCanvas;
